/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {
	Candidate,
	CandidateSets,
	HeaderResult,
	QuickSwitcherExecutableResult,
	QuickSwitcherResult,
} from '@app/stores/QuickSwitcherTypes';
import {FAVORITES_GUILD_ID} from '@fluxer/constants/src/AppConstants';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {QuickSwitcherResultTypes} from '@fluxer/constants/src/QuickSwitcherConstants';
import type {I18n} from '@lingui/core';
import {msg} from '@lingui/core/macro';

export type HeaderTitleType =
	| typeof QuickSwitcherResultTypes.USER
	| typeof QuickSwitcherResultTypes.GROUP_DM
	| typeof QuickSwitcherResultTypes.TEXT_CHANNEL
	| typeof QuickSwitcherResultTypes.VOICE_CHANNEL
	| typeof QuickSwitcherResultTypes.GUILD
	| typeof QuickSwitcherResultTypes.VIRTUAL_GUILD
	| typeof QuickSwitcherResultTypes.SETTINGS
	| typeof QuickSwitcherResultTypes.QUICK_ACTION;

function assertNever(value: never): never {
	throw new Error(`Unexpected quick switcher type: ${String(value)}`);
}

export function candidateToResult(
	candidate: Candidate,
	i18n: I18n,
	viewContext?: string,
): QuickSwitcherExecutableResult {
	switch (candidate.type) {
		case QuickSwitcherResultTypes.USER:
			return {
				type: QuickSwitcherResultTypes.USER,
				id: candidate.id,
				title: candidate.title,
				subtitle: candidate.subtitle,
				user: candidate.user,
				dmChannelId: candidate.dmChannelId,
				viewContext,
			};
		case QuickSwitcherResultTypes.GROUP_DM:
			return {
				type: QuickSwitcherResultTypes.GROUP_DM,
				id: candidate.id,
				title: candidate.title,
				subtitle: candidate.subtitle,
				channel: candidate.channel,
				viewContext,
			};
		case QuickSwitcherResultTypes.TEXT_CHANNEL:
		case QuickSwitcherResultTypes.VOICE_CHANNEL: {
			return {
				type: candidate.type,
				id: candidate.id,
				title: candidate.title,
				subtitle: getCandidateSubtitle(candidate.subtitle, i18n, viewContext),
				channel: candidate.channel,
				guild: candidate.guild,
				viewContext,
			};
		}
		case QuickSwitcherResultTypes.GUILD:
			return {
				type: QuickSwitcherResultTypes.GUILD,
				id: candidate.id,
				title: candidate.title,
				subtitle: candidate.subtitle,
				guild: candidate.guild,
			};
		case QuickSwitcherResultTypes.VIRTUAL_GUILD:
			return {
				type: QuickSwitcherResultTypes.VIRTUAL_GUILD,
				id: candidate.id,
				title: candidate.title,
				subtitle: candidate.subtitle,
				virtualGuildType: candidate.virtualGuildType,
			};
		case QuickSwitcherResultTypes.SETTINGS:
			return {
				type: QuickSwitcherResultTypes.SETTINGS,
				id: candidate.id,
				title: candidate.title,
				subtitle: candidate.subtitle,
				settingsTab: candidate.settingsTab,
				settingsSubtab: candidate.settingsSubtab,
			};
		case QuickSwitcherResultTypes.QUICK_ACTION:
			return {
				type: QuickSwitcherResultTypes.QUICK_ACTION,
				id: candidate.id,
				title: candidate.title,
				subtitle: candidate.subtitle,
				action: candidate.action,
			};
		default:
			return assertNever(candidate);
	}
}

export function createHeaderResult(id: string, title: string): HeaderResult {
	return {type: QuickSwitcherResultTypes.HEADER, id, title};
}

function getCandidateSubtitle(subtitle: string | undefined, i18n: I18n, viewContext?: string): string | undefined {
	if (viewContext === FAVORITES_GUILD_ID) {
		return i18n._(msg`Favorites`);
	}

	return subtitle;
}

export function getHeaderTitle(type: HeaderTitleType, i18n: I18n): string {
	switch (type) {
		case QuickSwitcherResultTypes.USER:
			return i18n._(msg`People`);
		case QuickSwitcherResultTypes.GROUP_DM:
			return i18n._(msg`Group messages`);
		case QuickSwitcherResultTypes.TEXT_CHANNEL:
			return i18n._(msg`Text channels`);
		case QuickSwitcherResultTypes.VOICE_CHANNEL:
			return i18n._(msg`Voice channels`);
		case QuickSwitcherResultTypes.GUILD:
		case QuickSwitcherResultTypes.VIRTUAL_GUILD:
			return i18n._(msg`Communities`);
		case QuickSwitcherResultTypes.SETTINGS:
			return i18n._(msg`Settings`);
		case QuickSwitcherResultTypes.QUICK_ACTION:
			return i18n._(msg`Quick Actions`);
		default:
			return assertNever(type);
	}
}

export function createResultFromChannel(
	channel: ChannelRecord,
	sets: CandidateSets,
	i18n: I18n,
	viewContext?: string,
): QuickSwitcherExecutableResult | null {
	switch (channel.type) {
		case ChannelTypes.DM:
		case ChannelTypes.DM_PERSONAL_NOTES: {
			const candidate = sets.userByChannelId.get(channel.id);
			return candidate ? candidateToResult(candidate, i18n, viewContext) : null;
		}
		case ChannelTypes.GROUP_DM: {
			const candidate = sets.groupDMByChannelId.get(channel.id);
			return candidate ? candidateToResult(candidate, i18n, viewContext) : null;
		}
		case ChannelTypes.GUILD_TEXT: {
			const candidate = sets.channelById.get(channel.id);
			if (candidate && candidate.type === QuickSwitcherResultTypes.TEXT_CHANNEL) {
				return candidateToResult(candidate, i18n, viewContext);
			}
			return null;
		}
		case ChannelTypes.GUILD_VOICE: {
			const candidate = sets.channelById.get(channel.id);
			if (candidate && candidate.type === QuickSwitcherResultTypes.VOICE_CHANNEL) {
				return candidateToResult(candidate, i18n, viewContext);
			}
			return null;
		}
		default:
			return null;
	}
}

export function getFirstSelectableIndex(results: ReadonlyArray<QuickSwitcherResult>): number {
	for (let i = 0; i < results.length; i += 1) {
		if (results[i].type !== QuickSwitcherResultTypes.HEADER) {
			return i;
		}
	}
	return -1;
}
